import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import MarketTable from "./MarketTable";
import { SYMBOL_TO_ID, useLiveMarket } from "./UseLivemarket";

import withdrawal from "../../assets/images/icons/withdrawal.png";
import deposit from "../../assets/images/icons/deposit.png";
import activity from "../../assets/images/icons/task.png";
import promotion from "../../assets/images/icons/earn.png";
import bots from "../../assets/images/icons/bot.png";

import { apiConnectorGet } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";

import "swiper/css";
import "swiper/css/pagination";
import ForexTicker from "./ForexTicker";

const TICKER_SYMBOLS = ["BTCUSDT", "ETHUSDT", "BNBUSDT"];

const COIN_LOGO = (baseSymbol) => {
  const symKey = `${baseSymbol.toUpperCase()}USDT`;
  const id = SYMBOL_TO_ID[symKey];

  if (id) {
    return `https://assets.coincap.io/assets/icons/${id}@2x.png`;
  }
  return `https://cryptoicons.org/api/color/${baseSymbol.toLowerCase()}/64`;
};

function CoinAvatar({ symbol }) {
  const [failed, setFailed] = useState(false);

  if (failed) {
    return (
      <div
        style={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          backgroundColor: "#1d4ed8",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          fontWeight: "bold",
          fontSize: 14,
        }}
      >
        {symbol[0]}
      </div>
    );
  }

  return (
    <img
      src={COIN_LOGO(symbol)}
      alt={symbol}
      onError={() => setFailed(true)}
      style={{
        // width: 20,
        // height: 20,
        borderRadius: "50%",
        objectFit: "contain",
        backgroundColor: "#1a2030",
      }}
      className="w-5 h-5 max-[350px]:w-4 max-[350px]:h-4 "
    />
  );
}

// ── Mini sparkline ─────────────────────────────────────────────────────────────
function TickerSparkline({ history, positive }) {
  const ref = useRef(null);
  const W = 80,
    H = 36;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || history.length < 2) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const min = Math.min(...history),
      max = Math.max(...history);
    const range = max - min || 1;
    const pad = 2;
    const pts = history.map((v, i) => ({
      x: pad + (i / (history.length - 1)) * (W - pad * 2),
      y: pad + (1 - (v - min) / range) * (H - pad * 2),
    }));

    const color = positive ? "#16C784" : "#ff5252";
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(
      0,
      positive ? "rgba(0,229,255,0.3)" : "rgba(255,82,82,0.3)",
    );
    grad.addColorStop(1, "rgba(0,0,0,0)");

    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      const mx = (pts[i - 1].x + pts[i].x) / 2;
      ctx.bezierCurveTo(mx, pts[i - 1].y, mx, pts[i].y, pts[i].x, pts[i].y);
    }
    ctx.lineTo(pts[pts.length - 1].x, H);
    ctx.lineTo(pts[0].x, H);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    for (let i = 1; i < pts.length; i++) {
      const mx = (pts[i - 1].x + pts[i].x) / 2;
      ctx.bezierCurveTo(mx, pts[i - 1].y, mx, pts[i].y, pts[i].x, pts[i].y);
    }
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }, [history, positive]);

  return <canvas ref={ref} style={{ width: W, height: H, display: "block" }} className="" />;
}

function TickerCard({ coin }) {
    if (!coin) {
        return null;
    }

    const change = Number(coin.priceChangePercent);
    const positive = change >= 0;
    const label = coin.symbol.replace("USDT", "");
    const baseSymbol = coin.symbol?.replace("USDT", "") ?? "?";

    const formatP = (p) => {
        const n = Number(p);
        if (n > 10000)
            return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
        if (n > 1) return n.toFixed(2);
        return n.toFixed(5);
    };

    return (
        <div
            style={{
                flex: 1,
                minWidth: 0,
                border: "1px solid rgba(235, 84, 222, 0.3)",
                borderRadius: "12px",
                padding: "2px 10px",
                display: "flex",
                flexDirection: "column",
                gap: "1px",
                boxShadow: "0 0 12px ",
                ml:1,
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexWrap: "wrap",
                }}
            >
                <CoinAvatar symbol={baseSymbol} />
                <span
                    style={{
                        color: "white",
                        fontWeight: "700",
                        // fontSize: "12px",
                    }}
                    className="text-[12px] max-[350px]:text-[11px]"
                >
                    {label}
                    <span
                        style={{
                            color: "#4b5563",
                        }}
                        className="text-[10px] ml-[2px] max-[350px]:text-[9px] max-[350px]:ml-[1px]"
                    >
                        USDT
                    </span>
                </span>
            </div>

            <div style={{
                // paddingLeft: "20px",
                paddingTop: "5px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}>
                <span
                    style={{
                        fontSize: "8px",
                        fontWeight: "700",
                        padding: "2px 0px",
                        borderRadius: "4px",
                        border: positive
                            ? "1px solid rgba(34,211,238,0.3)"
                            : "1px solid rgba(239,68,68,0.3)",
                        backgroundColor: positive
                            ? "rgba(34,211,238,0.1)"
                            : "rgba(239,68,68,0.1)",
                        color: positive ? "#16C784" : "#f87171",
                    }}
                >
                    {positive ? "+" : ""}
                    {change.toFixed(2)}%
                </span>
                <span style={{
                    fontSize: "11px",
                    fontWeight: "800",
                    letterSpacing: "-0.5px",
                    lineHeight: "1",
                    paddingLeft:"2px",
                    fontVariantNumeric: "tabular-nums",
                    color: positive ? "#16C784" : "#f87171",
                }}
                >
                    
                {formatP(coin.lastPrice)}
                </ span>
            </div>

            <div style={{ 
                // paddingLeft:"20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
             }}>
                <TickerSparkline
                    history={coin.history || []}
                    positive={positive}
                />
            </div>
        </div>
    );
}


// ── Dashboard ─────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { coins } = useLiveMarket();

  const { data: dashboard_data } = useQuery({
    queryKey: ["home_page_news"],
    queryFn: () => apiConnectorGet(endpoint.home_page_news),
    enabled: !!endpoint?.home_page_news,
  });

  const member_dashboard = dashboard_data?.data?.result?.[0].m01_nw_news?? "";
  const ACTION_ICONS = [
    { icon: deposit,    label: "Deposit",   to: "/TopupMember" },
    { icon: withdrawal, label: "Withdraw",  to: "/Withdrawal" },
    { icon: activity,   label: "Activity",  to: "/Activity" },
    { icon: promotion,  label: "Promotion", to: "/Promotion" },
    { icon: bots,  label: "Bots", to: "/Bots" },
  ];

  return (
    <div className="min-h-screen bg-[#0d1117] pb-24">

      <div className="px-3">
        {/* Hero slider */}
        <div className="pt-2 [&_.swiper-pagination-bullet-active]:bg-cyan-400">
          <Swiper
            modules={[Autoplay, Pagination]}
            slidesPerView={1}
            loop
            autoplay={{ delay: 5000 }}
            pagination={{ clickable: true }}
          >
            <SwiperSlide>
              <img src="./assets/vid.gif" alt="banner" className="w-full h-[150px] rounded-xl" />
            </SwiperSlide>
            <SwiperSlide>
              <img src="./assets/img1.png" alt="banner" className="w-full h-[150px] rounded-xl" />
            </SwiperSlide>
            <SwiperSlide>
              <img src="./assets/img2.png" alt="banner" className="w-full h-[150px] rounded-xl" />
            </SwiperSlide>
          </Swiper>
        </div>

        {/* Ticker cards below slider — powered by shared feed */}
        <div className="flex gap-2 mt-3">
          {TICKER_SYMBOLS.map((sym) => (
            <TickerCard key={sym} coin={coins[sym]} />
          ))}
        </div>

        {/* News marquee */}
        <div className="mt-3 rounded-lg bg-cyan-400/5 border border-cyan-900/30 overflow-hidden">
          <Link to="/News" className="flex items-center gap-2 px-3 py-1.5">
            <span
              className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0 animate-pulse"
              style={{ boxShadow: "0 0 6px #16C784" }}
            />
            <marquee className="flex-1">
              <span className="text-xs text-cyan-400/80">
                {member_dashboard}
              </span>
            </marquee>
          </Link>
        </div>
        <ForexTicker />

        {/* Action buttons */}
        <div className="flex justify-around mt-4">
          {ACTION_ICONS.map(({ icon, label, to }) => {
            const inner = (
              <>
                <img
                  src={icon}
                  alt={label}
                  className="w-9 h-9"
                  style={{
                    filter: "brightness(0) saturate(100%) invert(54%) sepia(99%) saturate(1814%) hue-rotate(165deg) brightness(101%) contrast(101%)",
                  }}
                />
                <span className="text-[10px] text-gray-400">{label}</span>
              </>
            );
            return to ? (
              <Link
                key={label}
                to={to}
                className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-cyan-400/5 transition-colors"
              >
                {inner}
              </Link>
            ) : (
              <button
                key={label}
                className="flex flex-col items-center gap-1.5 p-2 rounded-xl hover:bg-cyan-400/5 transition-colors"
              >
                {inner}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick buy heading */}
      <div className="px-3 mt-5 mb-3">
        <h2 className="text-white font-extrabold text-xl tracking-tight">
          Quick buy coins
        </h2>
        <p className="text-gray-500 text-xs mt-0.5">Safe and convenient</p>
      </div>

      <div className="px-3 mt-3">
        <MarketTable type="crypto" />
      </div>
    </div>
  );
}
